import ArrowBackIosNewRoundedIcon from "@mui/icons-material/ArrowBackIosNewRounded";
import ArrowForwardIosRoundedIcon from "@mui/icons-material/ArrowForwardIosRounded";
import { Box, Grid, IconButton } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import useAPI from "../../../api/hooks/useAPI";
import { ASSET_CARD_SIZE, AssetClass } from "../../../utils/constants";
import RegularAssetCard from "./components/cards/RegularAssetCard";
import SmartAssetCard from "./components/cards/SmartAssetCard";
import SuggestionAssetCard from "./components/cards/SuggestionAssetCard";

const AssetSlider = ({
  allCard = [],
  chosenAsset,
  handleOnAssetCardClick,
  entitiesInChosenAsset,
  hasPermission,
  displayMode,
}) => {
  const { trackEvent } = useAPI();
  const scrollContainerRef = useRef(null);
  const CARD_WIDTH = ASSET_CARD_SIZE.WIDTH;

  const [isAtStart, setIsAtStart] = useState(true);
  const [isAtEnd, setIsAtEnd] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);

  const scrollLeftFunc = () => {
    if (scrollContainerRef.current) {
      // Get the current scroll position
      const currentScrollLeft = scrollContainerRef.current.scrollLeft;

      // Calculate target position (one card width to the left)
      const targetScrollLeft = Math.max(0, currentScrollLeft - CARD_WIDTH);

      // Scroll to the exact position
      scrollContainerRef.current.scrollTo({
        left: targetScrollLeft,
        behavior: "smooth",
      });
    }
  };

  const scrollRight = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({
        left: CARD_WIDTH,
        behavior: "smooth",
      });
    }
  };

  const scrollToCard = (index) => {
    if (
      !allCard ||
      index < 0 ||
      index >= allCard.length ||
      !scrollContainerRef.current
    )
      return;

    const asset = allCard[index];
    if (!asset?.name || !asset?.asset_id) return;

    const cardWidth = CARD_WIDTH;
    const scrollPosition = index * cardWidth;
    scrollContainerRef.current.scrollTo({
      left: scrollPosition,
      behavior: "smooth",
    });
  };

  useEffect(() => {
    if (!chosenAsset || !allCard || !allCard.length) return;

    const index = allCard.findIndex(
      (asset) => asset?.asset_id === chosenAsset?.asset_id
    );
    if (index !== -1) {
      scrollToCard(index);
    }
  }, [chosenAsset?.asset_id, allCard]);

  useEffect(() => {
    const handleScroll = () => {
      if (!scrollContainerRef.current) return;

      const { scrollLeft, scrollWidth, clientWidth } =
        scrollContainerRef.current;
      setIsAtStart(scrollLeft === 0);
      setIsAtEnd(scrollLeft + clientWidth >= scrollWidth);
    };

    const handleResize = () => {
      handleScroll();
    };

    const handleMouseDown = (e) => {
      if (!scrollContainerRef.current) return;

      setIsDragging(true);
      setStartX(e.pageX - scrollContainerRef.current.offsetLeft);
      setScrollLeft(scrollContainerRef.current.scrollLeft);
    };

    const handleMouseMove = (e) => {
      if (!isDragging || !scrollContainerRef.current) return;

      e.preventDefault();
      const x = e.pageX - scrollContainerRef.current.offsetLeft;
      const walk = (x - startX) * 2; // Adjust the scroll speed
      scrollContainerRef.current.scrollLeft = scrollLeft - walk;
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    const handleWheel = (e) => {
      if (!scrollContainerRef.current) return;

      e.preventDefault();

      // Use deltaX for horizontal scrolling if available (trackpads)
      // If shift key is pressed, use deltaY for horizontal scrolling
      const scrollAmount = e.deltaX || (e.shiftKey ? e.deltaY : e.deltaY);

      scrollContainerRef.current.scrollBy({
        left: scrollAmount,
      });
    };

    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) return;

    const resizeObserver = new ResizeObserver(handleResize);

    scrollContainer.addEventListener("scroll", handleScroll);
    scrollContainer.addEventListener("mousedown", handleMouseDown);
    scrollContainer.addEventListener("mousemove", handleMouseMove);
    scrollContainer.addEventListener("mouseup", handleMouseUp);
    scrollContainer.addEventListener("mouseleave", handleMouseUp);
    scrollContainer.addEventListener("wheel", handleWheel, { passive: false });
    resizeObserver.observe(scrollContainer);

    // Initial check
    handleScroll();

    // Cleanup event listener and observer on component unmount
    return () => {
      scrollContainer.removeEventListener("scroll", handleScroll);
      scrollContainer.removeEventListener("mousedown", handleMouseDown);
      scrollContainer.removeEventListener("mousemove", handleMouseMove);
      scrollContainer.removeEventListener("mouseup", handleMouseUp);
      scrollContainer.removeEventListener("mouseleave", handleMouseUp);
      scrollContainer.removeEventListener("wheel", handleWheel);
      resizeObserver.unobserve(scrollContainer);
    };
  }, [isDragging, startX, scrollLeft]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "row",
        gap: 1,
      }}
    >
      <IconButton
        onClick={scrollLeftFunc}
        disabled={isAtStart}
        sx={{
          alignItems: "center",
          "&:hover": {
            backgroundColor: "transparent",
          },
          padding: "0px",
          transition: "none",
        }}
        disableRipple
      >
        <ArrowBackIosNewRoundedIcon
          sx={{
            width: "16px",
          }}
        />
      </IconButton>
      <Box
        ref={scrollContainerRef}
        sx={{
          display: "flex",
          flexDirection: "row",
          overflowX: "auto",

          gap: 2,
          padding: "6px",
          cursor: isDragging ? "grabbing" : "grab",
          "&::-webkit-scrollbar": {
            display: "none",
          },
          "-ms-overflow-style": "none",
          "scrollbar-width": "none",
        }}
      >
        {Array.isArray(allCard) &&
          allCard.map((asset, index) => {
            if (
              asset?.asset_id !== undefined &&
              asset?.cls === AssetClass.SMART
            ) {
              return (
                <Grid item key={asset.asset_id}>
                  <SmartAssetCard
                    asset={asset}
                    selected={chosenAsset?.asset_id === asset.asset_id}
                    onClick={() => handleOnAssetCardClick(asset)}
                    hasPermission={hasPermission}
                  />
                </Grid>
              );
            }
            if (
              asset?.asset_id !== undefined &&
              asset?.cls === AssetClass.REGULAR
            ) {
              return (
                <Grid item key={asset.asset_id}>
                  <RegularAssetCard
                    asset={asset}
                    entitiesInAsset={entitiesInChosenAsset}
                    onClick={() => handleOnAssetCardClick(asset)}
                    selected={chosenAsset?.asset_id === asset.asset_id}
                    key={asset.asset_id}
                    hasPermission={hasPermission}
                    displayMode={displayMode}
                  />
                </Grid>
              );
            }
            if (asset?.asset_suggestion_id) {
              return (
                <Grid item key={asset.asset_suggestion_id}>
                  <SuggestionAssetCard
                    suggestion={asset}
                    key={asset.asset_suggestion_id}
                    hasPermission={hasPermission}
                    displayMode={displayMode}
                  />
                </Grid>
              );
            }
            return null;
          })}
      </Box>
      <IconButton
        onClick={scrollRight}
        disabled={isAtEnd}
        sx={{
          alignItems: "center",
          "&:hover": {
            backgroundColor: "transparent",
          },
          padding: "0px",
          transition: "none",
        }}
        disableRipple
      >
        <ArrowForwardIosRoundedIcon
          sx={{
            width: "16px",
          }}
        />
      </IconButton>
    </Box>
  );
};

export default AssetSlider;
